import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  stressFactors, 
  getStressStatus, 
  teamStressData, 
  getStressRecommendations 
} from '../data/stressIndexLogic';

export const WorkplaceStressIndex = () => {
  const [selectedTeam, setSelectedTeam] = useState(teamStressData[0]);
  
  const status = useMemo(() => getStressStatus(selectedTeam.score), [selectedTeam]);
  const recommendations = useMemo(() => getStressRecommendations(selectedTeam.score), [selectedTeam]);

  // Bar Chart for Team Comparison
  const barOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'value', max: 100 },
    yAxis: { 
      type: 'category', 
      data: teamStressData.map(t => t.team),
      axisLine: { show: false },
      axisTick: { show: false }
    },
    series: [{
      name: 'Stress Score',
      type: 'bar',
      data: teamStressData.map(t => t.score),
      itemStyle: {
        color: (params) => {
          const val = params.value;
          if (val >= 81) return '#f43f5e';
          if (val >= 61) return '#f97316';
          if (val >= 31) return '#fbbf24';
          return '#10b981';
        },
        borderRadius: [0, 4, 4, 0]
      }
    }]
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 uppercase tracking-tight">Workplace Stress Index</h1>
          <p className="text-slate-500 mt-1">Måling av opplevd belastning, tidspress og restitusjon.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" icon={<SafeIcon name="Download" />}>PDF</Button>
          <Button variant="primary" icon={<SafeIcon name="Zap" />}>Kjør AI-analyse</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Dashboard */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-slate-900 border-none text-white overflow-hidden relative">
            <div className={`absolute top-0 right-0 w-64 h-64 bg-${status.color}-500/10 blur-[100px] -mr-20 -mt-20`}></div>
            <CardContent className="p-10 flex flex-col md:flex-row justify-between items-center gap-10 relative z-10">
              <div className="text-center md:text-left space-y-4">
                <p className="text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em]">Valgt Team: {selectedTeam.team}</p>
                <h2 className="text-7xl font-black tracking-tighter">
                  {selectedTeam.score}<span className={`text-${status.color}-500`}>%</span>
                </h2>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-${status.color}-400 text-xs font-bold uppercase`}>
                  <SafeIcon name="AlertTriangle" /> {status.label}
                </div>
              </div>
              <div className="flex-1 max-w-[280px] space-y-3">
                <p className="text-xs text-slate-400 leading-relaxed italic">
                  "Nivået for {selectedTeam.team} er i {status.status.toLowerCase()} sone. Dette indikerer et press som overgår teamets restitusjonskapasitet."
                </p>
                <div className="pt-2">
                  <Button variant="primary" size="sm" className={`w-full bg-${status.color}-600 hover:bg-${status.color}-700 border-none`}>
                    Se del-faktorer
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stress Factors Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {stressFactors.map((f) => (
              <Card key={f.id} className="border-none shadow-sm bg-white">
                <CardContent className="p-4 text-center">
                  <div className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 flex items-center justify-center mx-auto mb-2">
                    <SafeIcon name={f.icon} size={16} />
                  </div>
                  <h5 className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter mb-1 truncate">{f.name}</h5>
                  <div className="text-sm font-black text-slate-900">62%</div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Recommended Interventions */}
          <div className="space-y-4 pt-4">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider ml-1">Anbefalte Tiltak</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {recommendations.map((rec, i) => (
                <Card key={i} className="bg-white border-slate-200">
                  <CardContent className="p-5 flex gap-4 items-start">
                    <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
                      <SafeIcon name={rec.icon} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900">{rec.title}</h4>
                      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{rec.desc}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Team Comparison Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader title="Team Sammenligning" subtitle="Stress-indeks på tvers av avdelinger" icon={<SafeIcon name="BarChart" />} />
            <CardContent>
              <div className="h-64">
                <ReactECharts option={barOption} style={{ height: '100%' }} />
              </div>
              <div className="mt-6 divide-y divide-slate-100">
                {teamStressData.map((t) => (
                  <div 
                    key={t.team} 
                    onClick={() => setSelectedTeam(t)}
                    className={`p-3 flex items-center justify-between cursor-pointer transition-all rounded-lg ${selectedTeam.team === t.team ? 'bg-indigo-50' : 'hover:bg-slate-50'}`}
                  >
                    <span className="text-xs font-bold text-slate-700">{t.team}</span>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-black ${t.score >= 81 ? 'text-rose-500' : 'text-slate-400'}`}>{t.score}%</span>
                      <SafeIcon 
                        name={t.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                        className={t.trend === 'up' ? 'text-rose-500' : 'text-emerald-500'} 
                        size={12}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-indigo-600 text-white border-none">
            <CardContent className="p-6 space-y-4">
              <SafeIcon name="Info" className="text-3xl text-indigo-200" />
              <h4 className="font-bold">Ekspert-tips</h4>
              <p className="text-xs text-indigo-100 leading-relaxed italic">
                "Når stress-indeksen i Utvikling stiger samtidig som restitusjonsscoren faller, ser vi historisk en økning i korttidsfravær innen 14 dager."
              </p>
              <Button variant="secondary" className="w-full bg-white/10 border-white/20 text-white text-xs">
                Sjekk fraværsprognose
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};